import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  forwardRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { SuiFormControlBase } from '../shared/cva-helper';
import {
  SuiFormFieldControl,
  SUI_FORM_FIELD_CONTROL,
} from '../form-field/form-field-control';

/**
 * Single-file picker. The CVA value is the selected `File` (or `null`) — not a
 * filename string — so `Validators.required` and consumers operate on the real
 * payload directly. Mirrors sui-input's field box (border/focus/invalid) and adds
 * a styled trigger button whose surface sits one step off --input-bg (see the
 * file-input Tier 3 tokens).
 */
@Component({
  selector: 'sui-file-input',
  standalone: true,
  imports: [FaIconComponent],
  templateUrl: './sui-file-input.component.html',
  styleUrl: './sui-file-input.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SuiFileInputComponent),
      multi: true,
    },
    {
      provide: SUI_FORM_FIELD_CONTROL,
      useExisting: forwardRef(() => SuiFileInputComponent),
    },
  ],
})
export class SuiFileInputComponent
  extends SuiFormControlBase<File>
  implements SuiFormFieldControl
{
  private static _idCounter = 0;
  private readonly _uid = `sui-file-input-${++SuiFileInputComponent._idCounter}`;
  private _focused = false;

  @ViewChild('nativeInput') private nativeInput?: ElementRef<HTMLInputElement>;

  readonly controlType = 'file-input';

  protected readonly faCircleExclamation = faCircleExclamation;

  /** Comma-separated `accept` list forwarded to the native input (e.g. ".csv, .xlsx"). */
  @Input() accept = '';
  /** Trigger button text. English default; pass a translated string from the app. */
  @Input() buttonLabel = 'Choose File';
  /** Empty-state text shown next to the button. English default; translate at the call site. */
  @Input() placeholder = 'No file chosen';
  @Input() id = '';

  @Output() fileChange = new EventEmitter<File | null>();

  @Input()
  set disabled(val: boolean) {
    this.setDisabledState(val);
  }
  get disabled(): boolean {
    return this.isDisabled();
  }

  get effectiveId(): string {
    return this.id || this._uid;
  }

  get focused(): boolean {
    return this._focused;
  }

  /** The current File, or null when the CVA holds a non-File (e.g. empty-string reset). */
  get file(): File | null {
    const v = this.value();
    return v instanceof File ? v : null;
  }

  get empty(): boolean {
    return !this.file;
  }

  get errorState(): boolean {
    return !!(this.ngControl?.invalid && this.ngControl?.touched);
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this._focused = true;
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this._focused = false;
  }

  /** Open the native file dialog (button click / label click). */
  open(): void {
    if (this.isDisabled()) return;
    this.nativeInput?.nativeElement.click();
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.length ? input.files[0] : null;
    this.setValue(file);
    this.markTouched();
    this.fileChange.emit(file);
  }
}
